import { useEffect, useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';
import { Button, Checkbox, cn, Dialog, DialogContent } from '@proerp/ui';
import { useCurrentUser, usePermissions, useUpdateDashboardLayout } from '@/lib/useCurrentUser';
import { useToasts } from '@/lib/toast';
import { resolveWidgetOrder } from './widgets/layout';
import type { DashboardWidgetDef } from './widgets/types';

/**
 * Dashboard anpassen: Widgets ein-/ausblenden und in eigener Reihenfolge sortieren —
 * reine Anzeige-Präferenz am eigenen Benutzerkonto (geräteübergreifend, ADR 0062).
 * Es erscheinen nur Widgets, die der Nutzer laut Berechtigung sehen darf; der Server
 * erzwingt jede Datenquelle unabhängig vom Layout. Muster: ModuleVisibilityDialog.
 */
export function DashboardCustomizeDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const push = useToasts((state) => state.push);
  const { data } = useCurrentUser();
  const { can } = usePermissions();
  const update = useUpdateDashboardLayout();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [order, setOrder] = useState<DashboardWidgetDef[]>([]);

  const [dragId, setDragId] = useState<string | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const overIndexRef = useRef<number | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    setHidden(new Set(data?.data.dashboardLayout.hidden ?? []));
    setOrder(resolveWidgetOrder(data?.data.dashboardLayout.order ?? [], can));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);

  function toggle(id: string, visible: boolean) {
    setHidden((current) => {
      const next = new Set(current);
      if (visible) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onGripPointerDown(event: React.PointerEvent, id: string) {
    event.preventDefault();
    setDragId(id);
    const startIndex = order.findIndex((widget) => widget.id === id);
    overIndexRef.current = startIndex;
    setOverIndex(startIndex);
    const onMove = (move: PointerEvent) => {
      const rows = [...(listRef.current?.querySelectorAll('[data-widget-row]') ?? [])] as HTMLElement[];
      const index = rows.findIndex((row) => {
        const rect = row.getBoundingClientRect();
        return move.clientY < rect.top + rect.height / 2;
      });
      const next = index === -1 ? rows.length : index;
      overIndexRef.current = next;
      setOverIndex(next);
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
    const onUp = () => {
      cleanup();
      const target = overIndexRef.current;
      setDragId(null);
      setOverIndex(null);
      if (target === null) return;
      const from = order.findIndex((widget) => widget.id === id);
      let to = target > from ? target - 1 : target;
      to = Math.max(0, Math.min(order.length - 1, to));
      if (from !== -1 && from !== to) {
        setOrder((current) => {
          const next = [...current];
          const [moved] = next.splice(from, 1);
          next.splice(to, 0, moved);
          return next;
        });
      }
    };
    const onCancel = () => {
      cleanup();
      setDragId(null);
      setOverIndex(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  }

  async function save() {
    try {
      await update.mutateAsync({ order: order.map((widget) => widget.id), hidden: [...hidden] });
      push('success', 'Gespeichert.');
      onOpenChange(false);
    } catch (error) {
      push('danger', error instanceof Error ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Dashboard anpassen"
        description="Wähle, welche Widgets dein Dashboard zeigt, und ziehe sie in deine gewünschte Reihenfolge. Wirkt nur bei dir und ändert keine Berechtigungen."
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" isLoading={update.isPending} onClick={save}>
              Speichern
            </Button>
          </>
        }
      >
        <ul ref={listRef} className="flex flex-col gap-1">
          {order.map((widget, index) => (
            <li
              key={widget.id}
              data-widget-row
              className={cn(
                'flex items-center gap-2 rounded-md px-1 py-1 text-sm text-foreground',
                dragId === widget.id && 'opacity-50',
                overIndex === index && dragId && dragId !== widget.id && 'border-t-2 border-t-primary',
              )}
            >
              <button
                type="button"
                className="cursor-grab touch-none text-muted-foreground/60 hover:text-muted-foreground"
                onPointerDown={(event) => onGripPointerDown(event, widget.id)}
                aria-label={`${widget.title} verschieben`}
                title="Verschieben"
              >
                <GripVertical className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <label className="flex flex-1 items-center gap-2">
                <Checkbox
                  checked={!hidden.has(widget.id)}
                  onCheckedChange={(checked) => toggle(widget.id, checked === true)}
                />
                {widget.title}
              </label>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
